import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { PageLayout, PageSection } from "@/components/site/PageLayout";
import { Reveal, RevealGroup, Label, HEADING } from "@/components/site/Reveal";
import { ShareLinks } from "@/components/site/ShareLinks";
import { getCaseStudies, getCaseStudy } from "@/lib/cms";
import { absoluteUrl } from "@/lib/site";
import { ldScript, articleSchema, breadcrumbSchema } from "@/lib/schema";

export const Route = createFileRoute("/case-studies/$slug")({
  loader: ({ params }) => {
    const caseStudy = getCaseStudy(params.slug);
    if (!caseStudy) throw notFound();
    return caseStudy;
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Case Study Not Found · SunSmart Engineering" },
          { name: "robots", content: "noindex, nofollow" },
        ],
      };
    }
    const { name, "post-summary": summary, slug, "main-image": mainImage } = loaderData.fieldData;
    const title = `${name.trim()} · SunSmart Engineering`;
    const url = absoluteUrl(`/case-studies/${slug}`);
    const image = mainImage?.url ? absoluteUrl(mainImage.url) : undefined;
    const isDraft = loaderData.isDraft;
    return {
      meta: [
        { title },
        { name: "description", content: summary },
        { property: "og:title", content: title },
        { property: "og:description", content: summary },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        ...(image ? [{ property: "og:image", content: image }, { name: "twitter:image", content: image }] : []),
        ...(isDraft ? [{ name: "robots", content: "noindex, nofollow" }] : []),
      ],
      links: isDraft ? [] : [{ rel: "canonical", href: url }],
      scripts: [
        ldScript(
          articleSchema({
            headline: name.trim(),
            description: summary,
            url,
            image,
          }),
        ),
        ldScript(
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Case Studies", url: "/case-studies" },
            { name: name.trim(), url },
          ]),
        ),
      ],
    };
  },
  notFoundComponent: CaseStudyNotFound,
  component: CaseStudyPage,
});

function CaseStudyNotFound() {
  return (
    <PageLayout kicker="Case Studies" title="Not found">
      <PageSection>
        <p className="text-lg text-muted-foreground">
          We couldn't find that case study. It may have moved, or the link is out of date.
        </p>
        <Link
          to="/case-studies"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
        >
          Back to case studies <span>→</span>
        </Link>
      </PageSection>
    </PageLayout>
  );
}

function CaseStudyPage() {
  const caseStudy = Route.useLoaderData();
  const {
    name,
    type,
    "highlighted-quote": quote,
    "main-image": mainImage,
    "post-body": postBody,
    slug,
  } = caseStudy.fieldData;

  const all = getCaseStudies();
  const idx = all.findIndex((c) => c.fieldData.slug === slug);
  const prev = idx > 0 ? all[idx - 1] : undefined;
  const next = idx >= 0 && idx < all.length - 1 ? all[idx + 1] : undefined;

  return (
    <PageLayout kicker={type.trim()} title={name.trim()}>
      <PageSection label="01" title="Project overview">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="overflow-hidden rounded-sm border border-border bg-surface">
              <div className="aspect-[16/10]">
                <img
                  src={mainImage.url}
                  alt={name.trim()}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5">
            <RevealGroup stagger={70} maxStagger={200}>
              <Label n="—" text="Highlight" />
              <blockquote className="mt-6 border-l-2 border-sun pl-6">
                <p className={`${HEADING.h3} text-sun`}>{quote.trim()}</p>
              </blockquote>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
              >
                Start a similar project <span>→</span>
              </Link>
            </RevealGroup>
          </div>
        </div>

        <Reveal delay={120}>
          <div
            className="mt-16 max-w-[62ch] [&_h4]:font-display [&_h4]:mt-8 [&_h4]:text-sm [&_h4]:font-bold [&_h4]:uppercase [&_h4]:tracking-wider [&_h4]:text-sun [&_h4:first-child]:mt-0 [&_p]:mb-4 [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p:last-child]:mb-0 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:text-muted-foreground [&_ul]:space-y-1 [&_li]:leading-relaxed [&_strong]:text-foreground [&_strong]:font-semibold [&_em]:italic"
            dangerouslySetInnerHTML={{ __html: postBody }}
          />
        </Reveal>

        {/* Share + prev/next */}
        <div className="mt-16 border-t border-border pt-8">
          <ShareLinks title={name.trim()} path={`/case-studies/${slug}`} />
        </div>
        {(prev || next) && (
          <nav
            aria-label="More case studies"
            className="mt-8 flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:items-start sm:justify-between"
          >
            {prev ? (
              <Link
                to="/case-studies/$slug"
                params={{ slug: prev.fieldData.slug }}
                className="group max-w-xs"
              >
                <div className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">
                  ← Previous
                </div>
                <div className="mt-2 font-display text-base font-bold uppercase leading-tight group-hover:text-sun">
                  {prev.fieldData.name.trim()}
                </div>
              </Link>
            ) : (
              <span />
            )}
            {next && (
              <Link
                to="/case-studies/$slug"
                params={{ slug: next.fieldData.slug }}
                className="group max-w-xs sm:text-right"
              >
                <div className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">
                  Next →
                </div>
                <div className="mt-2 font-display text-base font-bold uppercase leading-tight group-hover:text-sun">
                  {next.fieldData.name.trim()}
                </div>
              </Link>
            )}
          </nav>
        )}
      </PageSection>

      <PageSection label="02" theme="surface">
        <div className="grid items-center gap-12 lg:grid-cols-[1.3fr_1fr]">
          <Reveal>
            <h2 className={HEADING.h2}>
              See more <span className="text-sun">case studies.</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 rounded-full border border-sun px-6 py-3 text-sm font-semibold text-sun hover:bg-sun hover:text-ink transition-colors lg:justify-self-end"
            >
              All case studies <span>→</span>
            </Link>
          </Reveal>
        </div>
      </PageSection>
    </PageLayout>
  );
}
